'use client';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ScoredStock } from '@/lib/compute';
import { StockTooltip } from './StockTooltip';
import { BacktestOutput } from '@/lib/backtest';
import { ProjectionHorizon } from '@/lib/projectScore';
import {
  PADDING, RANGE, GRID_CELLS, CELL_STEP, MAX_PINS,
  CORNERS, CORNERS_QM, FALLBACK_BENCHMARK,
  type PeriodKey, type PlotStock, type Rect,
  lerpColor, rectsOverlap,
  dotRadius, labelSize,
  backtestCellColor,
} from '@/lib/scatter2d-helpers';
import { ComparePanel } from './scatter2d/ComparePanel';
import { prodQuality } from '@/lib/composite-score';
import { SignalLegend } from './scatter2d/SignalLegend';
import { ProjectionSelector } from './scatter2d/ProjectionSelector';
import { PeriodSelector } from './scatter2d/PeriodSelector';
import { SvgDefs } from './scatter2d/SvgDefs';
import { StockDotsLayer } from './scatter2d/StockDotsLayer';
import { TickerLabelsLayer } from './scatter2d/TickerLabelsLayer';
import { Bullseye, AxisLabels, GridLines, CornerQuadrantLabels } from './scatter2d/SvgPlotChrome';
export { SignalLegend };  // re-export for backward compat

interface Props {
  stocks: ScoredStock[];
  highlightSymbol: string;
  pinnedSymbols: Set<string>;
  onTogglePin: (symbol: string) => void;
  onSelectStock: (symbol: string) => void;
  backtestData?: BacktestOutput | null;
  portfolioIsolated?: boolean;
  sectorIsolated?: boolean;
  enteredAlphaSymbols?: Set<string>;
  exitedAlphaSymbols?: Set<string>;
  projectionHorizon?: ProjectionHorizon;
  onProjectionChange?: (horizon: ProjectionHorizon) => void;
  yAxis?: 'valuation' | 'momentum';
}

export function ScatterPlot({
  stocks, highlightSymbol, pinnedSymbols, onTogglePin, onSelectStock,
  backtestData, portfolioIsolated, sectorIsolated,
  enteredAlphaSymbols, exitedAlphaSymbols,
  projectionHorizon, onProjectionChange, yAxis = 'valuation',
}: Props) {
  const yVal = (s: ScoredStock): number | null => yAxis === 'momentum' ? s.momentumScore : s.valuationScore;
  // Valuation: toY(+3) = bottom = cheap (top = expensive). Momentum: flip so +3 = top = high momentum.
  const yScreen = (v: number): number => yAxis === 'momentum' ? -v : v;
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('3yr');
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });
  const [hover, setHover] = useState<{ stock: ScoredStock; mx: number; my: number } | null>(null);

  // Zoom/Pan state
  const [viewBox, setViewBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; startVBX: number; startVBY: number; dragging: boolean } | null>(null);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: height });
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const vb = useMemo(
    () => viewBox ?? { x: 0, y: 0, w: dims.w, h: dims.h },
    [viewBox, dims.w, dims.h],
  );
  const zoomLevel = dims.w / vb.w;
  const isZoomed = zoomLevel > 1.01;

  const valid = stocks.filter(s => prodQuality(s) !== null && yVal(s) !== null);

  const plotW = Math.max(1, dims.w - PADDING.left - PADDING.right);
  const plotH = Math.max(1, dims.h - PADDING.top - PADDING.bottom);
  // 1.5:1 aspect ratio — quality (x) is 1.5x wider than valuation (y)
  const ASPECT = 1.5;
  let plotSizeX: number, plotSizeY: number;
  if (plotW / plotH > ASPECT) {
    plotSizeY = plotH;
    plotSizeX = plotH * ASPECT;
  } else {
    plotSizeX = plotW;
    plotSizeY = plotW / ASPECT;
  }
  const offsetX = (plotW - plotSizeX) / 2;
  const offsetY = (plotH - plotSizeY) / 2;

  const toX = (v: number) => PADDING.left + offsetX + ((v + RANGE) / (RANGE * 2)) * plotSizeX;
  const toY = (v: number) => PADDING.top + offsetY + ((v + RANGE) / (RANGE * 2)) * plotSizeY;

  const plotStocks: PlotStock[] = useMemo(() => valid.map(s => ({
    ...s,
    px: toX(prodQuality(s)!),
    py: toY(yScreen(yVal(s)!)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [valid, plotSizeX, plotSizeY, offsetX, offsetY, yAxis]);

  // Label collision detection
  const visibleLabels = useMemo(() => {
    const sorted = [...plotStocks].sort((a, b) => b.marketCap - a.marketCap);
    const placed: Rect[] = [];
    const visible = new Set<string>();
    const fontSize0 = 9.5;

    for (const s of sorted) {
      const isPinned = pinnedSymbols.has(s.symbol);
      const isHi = highlightSymbol && s.symbol === highlightSymbol;
      const isHov = hover?.stock.symbol === s.symbol;

      if (isPinned || isHi || isHov) {
        visible.add(s.symbol);
        continue;
      }

      const sz = labelSize(s.marketCap, false, false, false);
      const effectiveSz = sz / zoomLevel;
      const r = dotRadius(s.marketCap, false, false, false);
      const rect: Rect = {
        x: s.px + r + 2,
        y: s.py - effectiveSz * 0.5,
        w: s.symbol.length * fontSize0 * 0.62,
        h: effectiveSz * 1.2,
      };

      let collides = false;
      for (const p of placed) {
        if (rectsOverlap(rect, p)) { collides = true; break; }
      }

      if (!collides) {
        placed.push(rect);
        visible.add(s.symbol);
      }
    }
    return visible;
  }, [plotStocks, pinnedSymbols, highlightSymbol, hover?.stock.symbol, zoomLevel]);

  // Heatmap cells — backtest returns or positional gradient
  const heatCells = useMemo(() => {
    const cells: { x: number; y: number; w: number; h: number; fill: string; retLabel: string | null }[] = [];

    const bt = yAxis === 'valuation' ? backtestData : null;
    const periodData = bt?.periods?.[activePeriod];
    const gridMed = periodData?.gridMedians ?? bt?.gridMedians;
    const gridCnt = periodData?.gridCounts ?? bt?.gridCounts;
    const benchmark = periodData?.benchmark ?? bt?.benchmarkReturn ?? FALLBACK_BENCHMARK;
    const hasBacktest = bt && gridMed && gridCnt;

    for (let col = 0; col < GRID_CELLS; col++) {
      for (let row = 0; row < GRID_CELLS; row++) {
        const px = toX(-RANGE + col * CELL_STEP);
        const py = toY(RANGE - row * CELL_STEP);
        const pw = Math.abs(toX(-RANGE + (col + 1) * CELL_STEP) - px);
        const ph = Math.abs(toY(RANGE - (row + 1) * CELL_STEP) - py);

        let fill: string;
        let retLabel: string | null = null;

        if (hasBacktest) {
          const qCell = col;
          const vCell = GRID_CELLS - 1 - row;
          const ret = gridMed?.[qCell]?.[vCell] ?? null;
          const count = gridCnt?.[qCell]?.[vCell] ?? 0;
          const btColor = backtestCellColor(ret, count, benchmark);
          fill = btColor ?? '#f8f8f8';
          if (ret !== null && count >= 3) {
            retLabel = `${ret >= 0 ? '+' : ''}${(ret * 100).toFixed(0)}%`;
          }
        } else {
          const tx = (col + 0.5) / GRID_CELLS;
          const ty = (GRID_CELLS - 1 - row + 0.5) / GRID_CELLS;
          const P = yAxis === 'momentum' ? CORNERS_QM : CORNERS;
          const c = lerpColor(P.topLeft, P.topRight, P.bottomLeft, P.bottomRight, tx, ty);
          fill = c;
        }

        cells.push({ x: px, y: py, w: pw, h: ph, fill, retLabel });
      }
    }
    return cells;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotSizeX, plotSizeY, offsetX, offsetY, backtestData, activePeriod, yAxis]);

  // Bullseye rings — concentric ellipses centered on mean of plotted stocks
  const bullseye = useMemo(() => {
    if (plotStocks.length < 3) return null;
    const meanX = plotStocks.reduce((s, p) => s + prodQuality(p)!, 0) / plotStocks.length;
    const meanY = plotStocks.reduce((s, p) => s + (yVal(p) ?? 0), 0) / plotStocks.length;
    const distances = plotStocks.map(p => Math.sqrt((prodQuality(p)! - meanX) ** 2 + ((yVal(p) ?? 0) - meanY) ** 2));
    const meanDist = distances.reduce((s, d) => s + d, 0) / distances.length;
    const stdDev = Math.sqrt(distances.reduce((s, d) => s + (d - meanDist) ** 2, 0) / distances.length);
    const scaleX = plotSizeX / (RANGE * 2);
    const scaleY = plotSizeY / (RANGE * 2);
    return {
      cx: toX(meanX), cy: toY(yScreen(meanY)),
      innerRx: 0.25 * stdDev * scaleX, innerRy: 0.25 * stdDev * scaleY,
      outerRx: 1.0 * stdDev * scaleX, outerRy: 1.0 * stdDev * scaleY,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotStocks, plotSizeX, plotSizeY]);

  // Convert screen coords to SVG coords
  const screenToSvg = useCallback((clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;
    return {
      x: vb.x + (screenX / dims.w) * vb.w,
      y: vb.y + (screenY / dims.h) * vb.h,
    };
  }, [vb, dims]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (dragRef.current?.dragging) {
      const rect = e.currentTarget.getBoundingClientRect();
      const dx = (e.clientX - dragRef.current.startX) / rect.width * vb.w;
      const dy = (e.clientY - dragRef.current.startY) / rect.height * vb.h;
      setViewBox({
        ...vb,
        x: dragRef.current.startVBX - dx,
        y: dragRef.current.startVBY - dy,
      });
      return;
    }

    const svgPt = screenToSvg(e.clientX, e.clientY);
    let best: PlotStock | null = null;
    let bestDist = (20 / zoomLevel) ** 2;
    for (const s of plotStocks) {
      const d = (s.px - svgPt.x) ** 2 + (s.py - svgPt.y) ** 2;
      if (d < bestDist) { bestDist = d; best = s; }
    }
    setHover(best ? { stock: best, mx: e.clientX, my: e.clientY } : null);
  }, [plotStocks, screenToSvg, vb, zoomLevel]);

  const handleMouseLeave = useCallback(() => {
    setHover(null);
    dragRef.current = null;
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const cur = viewBox ?? { x: 0, y: 0, w: dims.w, h: dims.h };
    const newW = Math.max(dims.w / 5, Math.min(dims.w, cur.w / factor));
    const newH = Math.max(dims.h / 5, Math.min(dims.h, cur.h / factor));

    const rect = e.currentTarget.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;

    const newX = cur.x + (cur.w - newW) * mx;
    const newY = cur.y + (cur.h - newH) * my;

    setViewBox({ x: newX, y: newY, w: newW, h: newH });
  }, [viewBox, dims]);

  const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const cur = viewBox ?? { x: 0, y: 0, w: dims.w, h: dims.h };
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startVBX: cur.x,
      startVBY: cur.y,
      dragging: false,
    };
  }, [viewBox, dims]);

  const handleMouseUp = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;

    if (drag) {
      const dist = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY);
      if (dist <= 3) {
        const svgPt = screenToSvg(e.clientX, e.clientY);
        let best: PlotStock | null = null;
        let bestDist = (20 / zoomLevel) ** 2;
        for (const s of plotStocks) {
          const d = (s.px - svgPt.x) ** 2 + (s.py - svgPt.y) ** 2;
          if (d < bestDist) { bestDist = d; best = s; }
        }
        if (best) {
          onSelectStock(best.symbol);
          if (pinnedSymbols.has(best.symbol) || pinnedSymbols.size < MAX_PINS) {
            onTogglePin(best.symbol);
          }
        }
      }
    }
  }, [screenToSvg, plotStocks, pinnedSymbols, onTogglePin, onSelectStock, zoomLevel]);

  const handleDragMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (dragRef.current && !dragRef.current.dragging) {
      const dist = Math.hypot(e.clientX - dragRef.current.startX, e.clientY - dragRef.current.startY);
      if (dist > 3) {
        dragRef.current.dragging = true;
      }
    }
    handleMouseMove(e);
  }, [handleMouseMove]);

  const resetZoom = useCallback(() => setViewBox(null), []);

  const ticks = [-3, -2, -1, 0, 1, 2, 3];

  // Z-order: small → large → pinned → highlighted
  const sortedStocks = useMemo(() =>
    [...plotStocks].sort((a, b) => {
      const aPin = pinnedSymbols.has(a.symbol) ? 1 : 0;
      const bPin = pinnedSymbols.has(b.symbol) ? 1 : 0;
      const aHi = (highlightSymbol && a.symbol === highlightSymbol) ? 2 : aPin;
      const bHi = (highlightSymbol && b.symbol === highlightSymbol) ? 2 : bPin;
      if (aHi !== bHi) return aHi - bHi;
      return a.marketCap - b.marketCap;
    }),
  [plotStocks, highlightSymbol, pinnedSymbols]);

  const cornerLabels = yAxis === 'momentum' ? [
    { x: PADDING.left + offsetX + 10, y: PADDING.top + offsetY + 16, text: 'Low Quality + High Momentum', color: '#a855f7' },
    { x: PADDING.left + offsetX + plotSizeX - 10, y: PADDING.top + offsetY + 16, text: 'High Quality + High Momentum', color: '#16a34a', anchor: 'end' as const },
    { x: PADDING.left + offsetX + 10, y: PADDING.top + offsetY + plotSizeY - 8, text: 'Low Quality + Low Momentum', color: '#ef4444' },
    { x: PADDING.left + offsetX + plotSizeX - 10, y: PADDING.top + offsetY + plotSizeY - 8, text: 'High Quality + Low Momentum', color: '#ca8a04', anchor: 'end' as const },
  ] : [
    { x: PADDING.left + offsetX + 10, y: PADDING.top + offsetY + 16, text: 'Low Quality + More Expensive', color: '#a855f7' },
    { x: PADDING.left + offsetX + plotSizeX - 10, y: PADDING.top + offsetY + 16, text: 'High Quality + More Expensive', color: '#3b82f6', anchor: 'end' as const },
    { x: PADDING.left + offsetX + 10, y: PADDING.top + offsetY + plotSizeY - 8, text: 'Low Quality + Less Expensive', color: '#ca8a04' },
    { x: PADDING.left + offsetX + plotSizeX - 10, y: PADDING.top + offsetY + plotSizeY - 8, text: 'High Quality + Less Expensive', color: '#16a34a', anchor: 'end' as const },
  ];

  const pinnedStocks = useMemo(() =>
    stocks.filter(s => pinnedSymbols.has(s.symbol)),
  [stocks, pinnedSymbols]);

  return (
    <div ref={containerRef} className="relative flex-1" style={{ background: '#ffffff' }}>
      {onProjectionChange && (
        <ProjectionSelector projectionHorizon={projectionHorizon} onProjectionChange={onProjectionChange} />
      )}
      {projectionHorizon && projectionHorizon !== 'now' && (
        <div className="absolute top-10 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <div className="text-[11px] font-mono font-semibold px-3 py-1 rounded" style={{ color: 'rgba(168, 85, 247, 0.5)' }}>
            Projected {projectionHorizon} Forward
          </div>
        </div>
      )}
      {backtestData && (
        <PeriodSelector backtestData={backtestData} activePeriod={activePeriod} onPeriodChange={setActivePeriod} />
      )}

      <svg
        width={dims.w}
        height={dims.h}
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        onMouseMove={handleDragMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        className={dragRef.current?.dragging ? 'cursor-grabbing' : 'cursor-crosshair'}
        style={{ userSelect: 'none' }}
      >
        <SvgDefs plotX={PADDING.left + offsetX} plotY={PADDING.top + offsetY} plotW={plotSizeX} plotH={plotSizeY} />

        {/* Plot background */}
        <rect x={PADDING.left + offsetX} y={PADDING.top + offsetY} width={plotSizeX} height={plotSizeY} fill="#ffffff" rx={6} />

        {/* Heatmap cells */}
        <g clipPath="url(#plotClip)">
          {heatCells.map((c, i) => (
            <rect key={i} x={c.x} y={c.y} width={c.w} height={c.h} fill={c.fill} opacity={backtestData ? 1 : 0.15} />
          ))}
          {backtestData && !portfolioIsolated && !sectorIsolated && heatCells.map((c, i) => c.retLabel ? (
            <text key={`lbl${i}`}
              x={c.x + c.w / 2} y={c.y + c.h / 2 + 4}
              fill="rgba(255,255,255,0.9)"
              fontSize={Math.min(11, c.w / 5)}
              textAnchor="middle"
              fontFamily="var(--font-mono), monospace"
              fontWeight={600}
              opacity={0.85}>
              {c.retLabel}
            </text>
          ) : null)}
        </g>

        <GridLines ticks={ticks} toX={toX} toY={toY} offsetX={offsetX} offsetY={offsetY} plotSizeX={plotSizeX} plotSizeY={plotSizeY} padding={PADDING} />

        <CornerQuadrantLabels labels={cornerLabels} />

        {/* Plot border */}
        <rect x={PADDING.left + offsetX} y={PADDING.top + offsetY} width={plotSizeX} height={plotSizeY}
          fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth={1} rx={6} />

        {bullseye && (
          <g clipPath="url(#plotClip)">
            <Bullseye {...bullseye} />
          </g>
        )}

        <StockDotsLayer
          sortedStocks={sortedStocks}
          highlightSymbol={highlightSymbol}
          pinnedSymbols={pinnedSymbols}
          hoveredSymbol={hover?.stock.symbol ?? null}
          enteredAlphaSymbols={enteredAlphaSymbols}
          exitedAlphaSymbols={exitedAlphaSymbols}
        />

        <TickerLabelsLayer
          sortedStocks={sortedStocks}
          highlightSymbol={highlightSymbol}
          pinnedSymbols={pinnedSymbols}
          hoveredSymbol={hover?.stock.symbol ?? null}
          visibleLabels={visibleLabels}
          enteredAlphaSymbols={enteredAlphaSymbols}
          exitedAlphaSymbols={exitedAlphaSymbols}
        />

        <AxisLabels ticks={ticks} toX={toX} toY={toY} offsetX={offsetX} offsetY={offsetY} plotSizeX={plotSizeX} plotSizeY={plotSizeY} padding={PADDING} yAxis={yAxis} />
      </svg>

      <SignalLegend
        enteredAlphaSymbols={enteredAlphaSymbols ?? new Set()}
        exitedAlphaSymbols={exitedAlphaSymbols ?? new Set()}
        stocks={stocks}
      />

      {isZoomed && (
        <button
          onClick={resetZoom}
          className="absolute top-3 right-3 z-10 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold
                     text-[#e2e8f0] bg-[#0d1117]/80 border border-[#1e293b]
                     hover:bg-[#0d1117] hover:text-white transition-colors duration-150 cursor-pointer"
        >
          Reset Zoom
        </button>
      )}

      {pinnedStocks.length > 0 && (
        <ComparePanel pinnedStocks={pinnedStocks} maxPins={MAX_PINS} onTogglePin={onTogglePin} />
      )}

      {hover && (() => {
        const tooltipW = 340;
        const tooltipH = 260;
        const nearRight = hover.mx > window.innerWidth - tooltipW - 24;
        const nearBottom = hover.my > window.innerHeight - tooltipH - 24;
        const nearTop = hover.my < tooltipH + 24;
        const flipY = nearBottom && !nearTop;

        let left = nearRight ? hover.mx - tooltipW - 16 : hover.mx + 16;
        let top = flipY ? hover.my - tooltipH - 12 : hover.my - 12;

        left = Math.max(4, Math.min(left, window.innerWidth - tooltipW - 4));
        top = Math.max(4, Math.min(top, window.innerHeight - tooltipH - 4));

        return (
          <div className="fixed pointer-events-none z-50" style={{ left, top }}>
            <StockTooltip stock={hover.stock} x={0} y={0} flipX={false} flipY={false} />
          </div>
        );
      })()}
    </div>
  );
}
